import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";

const FORM_ENDPOINT = "https://readdy.ai/api/form/daomhr6s79n0eakefh40";

type Status = "idle" | "submitting" | "success" | "error";

export default function InquiryForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("company_alt") ?? "").trim();
    if (honeypot) {
      setFormError("");
      setStatus("success");
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "company_alt") return;
      const str = typeof value === "string" ? value : "";
      if (str.trim() === "") return;
      params.append(key, str);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const looksLikeSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !looksLikeSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
        return;
      }

      setStatus("error");
      setFormError(
        serverMsg && !looksLikeSpam
          ? serverMsg
          : "We could not send your inquiry right now. Please try again or call the clinic directly.",
      );
    } catch {
      setStatus("error");
      setFormError(
        "We could not reach the clinic form. Please check your connection and try again.",
      );
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors duration-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

  return (
    <section id="inquiry" className="w-full bg-background-100 border-y border-background-200 px-4 md:px-6 py-16 md:py-24">
      <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-4">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">
              <span className="w-6 h-px bg-primary-400" />
              Inquiry form
            </span>
            <h2 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              Send an Inquiry
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              Use this form to ask general questions about our services and clinic availability. A
              member of the clinic will follow up using the contact details you provide.
            </p>

            <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-4">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-md bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-lock-line text-accent-700" />
                </span>
                <p className="text-xs leading-relaxed text-foreground-700">
                  Please do not include sensitive medical information in this contact form. Contact
                  the clinic directly for private medical questions.
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-8" delay={120}>
          <div className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8">
            <form
              id="clinic-inquiry-form"
              data-readdy-form
              onSubmit={handleSubmit}
              noValidate
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="full_name"
                    className="block text-sm font-medium text-foreground-800 mb-2"
                  >
                    Full Name
                  </label>
                  <input
                    id="full_name"
                    name="full_name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="Your full name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-foreground-800 mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="(000) 000-0000"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-foreground-800 mb-2"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className={inputClass}
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-foreground-800 mb-2"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  placeholder="How can the clinic help you? Please keep medical details private."
                  className={`${inputClass} resize-none`}
                />
                <p className="mt-2 text-xs text-foreground-500">Maximum 500 characters.</p>
              </div>

              <div className="ffc-field-note" aria-hidden="true">
                <label htmlFor="company_alt">Company</label>
                <input
                  id="company_alt"
                  name="company_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {status === "error" && formError && (
                <div className="rounded-md border border-primary-300 bg-primary-50 px-4 py-3">
                  <p className="text-sm text-foreground-800">{formError}</p>
                </div>
              )}

              {status === "success" && (
                <div className="rounded-md border border-accent-300 bg-accent-50 px-4 py-3">
                  <p className="text-sm text-foreground-800">
                    Thank you. Your inquiry has been sent. The clinic will follow up using the
                    contact details you provided.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors duration-200 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                {status === "submitting" ? "Sending..." : "Send Inquiry"}
              </button>

              <p className="text-xs leading-relaxed text-foreground-500">
                This form is for general inquiries only and does not create a doctor-patient
                relationship. For medical emergencies, call 911.
              </p>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
}